import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { IonIcon } from '@ionic/react';
import { caretDown, caretUp } from 'ionicons/icons';
import { buildMarketValueChartData, type MarketValueHistoryPoint } from './marketValueHistoryUtils';

interface MarketValueHistoryProps {
  marketValue?: number | null;
  previousMarketValue?: number | null;
  history: MarketValueHistoryPoint[];
}

const formatMarketValue = (value: number) => `${value.toLocaleString('es-ES')}€`;

const formatAxisDate = (timestamp: number) =>
  new Intl.DateTimeFormat('es-ES', {
    month: 'short',
  }).format(new Date(timestamp));

const formatTooltipDate = (timestamp: number) =>
  new Intl.DateTimeFormat('es-ES', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(new Date(timestamp));

const MarketValueTrend = ({
  marketValue,
  previousMarketValue,
}: Pick<MarketValueHistoryProps, 'marketValue' | 'previousMarketValue'>) => {
  if (
    marketValue === null ||
    marketValue === undefined ||
    previousMarketValue === null ||
    previousMarketValue === undefined ||
    marketValue === previousMarketValue
  ) {
    return <span className="text-xl font-bold text-gray-700">=</span>;
  }

  if (marketValue > previousMarketValue) {
    return <IonIcon aria-label="Valor en subida" icon={caretUp} className="text-xl text-green-600" />;
  }

  return <IonIcon aria-label="Valor en bajada" icon={caretDown} className="text-xl text-red-600" />;
};

export const MarketValueHistory = ({
  marketValue,
  previousMarketValue,
  history,
}: MarketValueHistoryProps) => {
  const { data, xDomain, yDomain } = buildMarketValueChartData(history);

  return (
    <section className="bg-gray-50 rounded p-4 space-y-3" aria-labelledby="market-value-title">
      <h3 id="market-value-title" className="font-bold text-base">
        Valor de mercado
      </h3>

      <div className="flex items-center gap-2">
        <span className="text-2xl font-bold text-gray-900">
          {marketValue !== null && marketValue !== undefined ? formatMarketValue(marketValue) : '-'}
        </span>
        <MarketValueTrend marketValue={marketValue} previousMarketValue={previousMarketValue} />
      </div>

      <div className="h-44 w-full" data-testid="market-value-chart">
        {data.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
              <CartesianGrid stroke="#e5e7eb" strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="timestamp"
                domain={xDomain}
                tickFormatter={formatAxisDate}
                tickLine={false}
                type="number"
                stroke="#6b7280"
                fontSize={11}
              />
              <YAxis
                allowDecimals={false}
                domain={yDomain}
                tickFormatter={(value) => `${Number(value).toLocaleString('es-ES')}€`}
                tickLine={false}
                width={58}
                stroke="#6b7280"
                fontSize={11}
              />
              <Tooltip
                formatter={(value) => [formatMarketValue(Number(value)), 'Valor']}
                labelFormatter={(value) => formatTooltipDate(Number(value))}
              />
              <Line
                dataKey="marketValue"
                dot={{ r: 3 }}
                isAnimationActive={false}
                name="Valor"
                stroke="#2563eb"
                strokeWidth={2.5}
                type="monotone"
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="flex h-full items-center justify-center text-sm text-gray-600">
            Sin datos de valor de mercado.
          </div>
        )}
      </div>
    </section>
  );
};
